import React, { useRef } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Container, Grid, Typography } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import Instagram from '@mui/icons-material/Instagram';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import Header from './Header';
import Footer from './Footer';
import BookDemo from './BookDemo';

const blue = '#0859C1';
const ink = '#101C2E';
const muted = '#536277';
const line = '#E1E9F3';

const teams = [
  [PersonRoundedIcon, 'Just starting', '1–4 people', 'For a founder or a small team that wants every lead in one place.'],
  [GroupRoundedIcon, 'Growing team', '5–10 people', 'For teams that need lead routing, follow-up reminders and site visit tracking.'],
  [GroupsRoundedIcon, 'Sales floor', '11–25 people', 'For managers who need team reports, attendance and channel partners.'],
  [ApartmentRoundedIcon, 'Large team', '26+ people', 'For builders and mandate firms running many projects and partners.'],
];

const included = [
  'Lead capture from portals, ads, website, WhatsApp and walk-ins',
  'Automatic lead assignment to your salespeople',
  'Sales pipeline with your own stages',
  'Site visit scheduling and reminders',
  'Projects, units and buyer matching',
  'Android and iPhone app for your team',
  'Reports and daily summaries',
  'Setup, training and moving your existing leads',
];

const addOns = [
  [WhatsApp, 'WhatsApp Business', 'Automatic replies, reminders and broadcasts from your own business number.', 'wa'],
  [Instagram, 'Instagram & Facebook', 'Turn comments and messages into leads, with automatic replies.', 'ig'],
  [PhoneInTalkRoundedIcon, 'AI Calling', 'Free 7-day trial with ₹500 credit, then ₹5 per connected minute.', 'blue'],
  [SmartToyRoundedIcon, 'AI Sales Agent', 'Covers the leads your team can’t reach in time. Pay only for what it does.', 'violet'],
];
const tones = { wa: ['#DCF8E6', '#128C4A'], ig: ['#FCE7F3', '#C13584'], blue: ['#E1F0FD', blue], violet: ['#EFEAFD', '#5B3CC4'] };

const steps = [
  ['Tell us your team size', 'Pick the size closest to your team. No commitment.'],
  ['See it on your own leads', 'A 20-minute demo with your projects and lead sources.'],
  ['Get your price', 'A simple quote for your team and the add-ons you want.'],
];

const faqs = [
  { q: 'How is the price worked out?', a: 'Mainly by how many people on your team use AcoLead, plus any add-ons you switch on, such as WhatsApp or AI Calling. Tell us your team size and we’ll send you a clear quote.' },
  { q: 'Can I add more users later?', a: 'Yes. Add users whenever your team grows.' },
  { q: 'Are WhatsApp, Instagram and AI Calling included?', a: 'They are add-ons you switch on when you need them. AI Calling starts with a 7-day trial and ₹500 credit, then ₹5 per connected minute.' },
  { q: 'Is training included?', a: 'Yes. We set up AcoLead with you and train your admins and sales team as part of getting started.' },
  { q: 'Can you move our existing leads?', a: 'Yes. We import your leads and history from your current CRM or Excel.' },
  { q: 'Can I see the product before deciding?', a: 'Yes. Book a demo and we’ll walk you through the features that matter to your business.' },
];

const PricingPage = () => {
  const demoRef = useRef(null);
  const goToDemo = () => {
    const el = demoRef.current;
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', '@media (max-width:1023.95px)': { pb: 8 } }}>
      <Header />
      <Box component="main" sx={{ flex: 1 }}>
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#F2F6FC', borderBottom: `1px solid ${line}` }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: blue, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 800, fontSize: 11, mb: 1.5 }}>AcoLead pricing</Typography>
            <Typography component="h1" sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: ink, fontWeight: 800, fontSize: { xs: 36, md: 48 }, lineHeight: 1.12, letterSpacing: '-.04em', mb: 2 }}>Simple pricing that grows with your team</Typography>
            <Box sx={{ width: 48, height: 4, bgcolor: '#3B9D1D', borderRadius: 3, mx: 'auto', mb: 2 }} />
            <Typography sx={{ color: muted, fontSize: 17, lineHeight: 1.65, maxWidth: 640, mx: 'auto', mb: 3 }}>
              You pay for the people on your team who use AcoLead. Everything your sales team needs is included, and you add WhatsApp or AI only when you want them.
            </Typography>
            <Button onClick={goToDemo} variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ bgcolor: blue, px: 3, py: 1.3, borderRadius: 2, fontWeight: 800, textTransform: 'none', fontSize: 15, boxShadow: 'none', '&:hover': { bgcolor: '#06489D' } }}>Get My Price</Button>
            <Typography sx={{ color: muted, fontSize: 13, mt: 1.4 }}>Free demo · No commitment</Typography>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Typography component="h2" sx={{ color: ink, fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: { xs: 26, md: 32 }, fontWeight: 800, textAlign: 'center', mb: .8 }}>How big is your sales team?</Typography>
          <Typography sx={{ color: muted, fontSize: 15, textAlign: 'center', mb: 3.5 }}>Pick the closest size and we’ll send you a price for it.</Typography>
          <Grid container spacing={2}>
            {teams.map(([Icon, name, size, best]) => <Grid item xs={12} sm={6} md={3} key={name}>
              <Box component="button" type="button" onClick={goToDemo} sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex', flexDirection: 'column', height: '100%', width: '100%', p: 2.6, borderRadius: 3, border: `1px solid ${line}`, bgcolor: '#fff', transition: 'border-color .2s, box-shadow .2s, transform .2s', '&:hover, &:focus-visible': { borderColor: '#9FC3EF', boxShadow: '0 14px 32px rgba(8,89,193,.10)', transform: 'translateY(-2px)' } }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#E1F0FD', color: blue, display: 'grid', placeItems: 'center', mb: 1.6 }}><Icon /></Box>
                <Typography sx={{ color: ink, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 18 }}>{name}</Typography>
                <Typography sx={{ color: blue, fontWeight: 800, fontSize: 14, mb: 1 }}>{size}</Typography>
                <Typography sx={{ color: muted, fontSize: 13.5, lineHeight: 1.55, flex: 1 }}>{best}</Typography>
                <Typography sx={{ color: blue, fontWeight: 800, fontSize: 14, mt: 2, display: 'flex', alignItems: 'center', gap: .5 }}>Get my price <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} /></Typography>
              </Box>
            </Grid>)}
          </Grid>
        </Container>

        <Box sx={{ bgcolor: '#F8FBFF', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Typography component="h2" sx={{ color: ink, fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: { xs: 24, md: 28 }, fontWeight: 800, mb: 2 }}>Included in every plan</Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.2 }}>
                  {included.map((item) => <Box component="li" key={item} sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start', color: ink, fontSize: 15 }}><CheckCircleRoundedIcon sx={{ color: '#3B9D1D', fontSize: 20, mt: '1px' }} />{item}</Box>)}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography component="h2" sx={{ color: ink, fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: { xs: 24, md: 28 }, fontWeight: 800, mb: 2 }}>Add only what you need</Typography>
                <Box sx={{ display: 'grid', gap: 1.2 }}>
                  {addOns.map(([Icon, name, desc, t]) => <Box key={name} sx={{ display: 'flex', gap: 1.5, p: 1.8, borderRadius: 2.5, border: `1px solid ${line}`, bgcolor: '#fff' }}>
                    <Box sx={{ width: 40, height: 40, flex: '0 0 40px', borderRadius: '50%', bgcolor: tones[t][0], color: tones[t][1], display: 'grid', placeItems: 'center' }}><Icon sx={{ fontSize: 21 }} /></Box>
                    <Box><Typography sx={{ color: ink, fontWeight: 800, fontSize: 15 }}>{name}</Typography><Typography sx={{ color: muted, fontSize: 13.5, lineHeight: 1.5 }}>{desc}</Typography></Box>
                  </Box>)}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Typography component="h2" sx={{ color: ink, fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: { xs: 24, md: 28 }, fontWeight: 800, textAlign: 'center', mb: 3 }}>Getting your price takes three steps</Typography>
          <Grid container spacing={2}>
            {steps.map(([t, b], i) => <Grid item xs={12} md={4} key={t}><Box sx={{ height: '100%', p: 2.4, borderRadius: 3, border: `1px solid ${line}`, bgcolor: '#fff' }}>
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: blue, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, mb: 1.2 }}>{i + 1}</Box>
              <Typography sx={{ color: ink, fontWeight: 800, fontSize: 16, mb: .5 }}>{t}</Typography>
              <Typography sx={{ color: muted, fontSize: 14, lineHeight: 1.55 }}>{b}</Typography>
            </Box></Grid>)}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 3 }}><Button onClick={goToDemo} variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ bgcolor: blue, px: 3, py: 1.2, borderRadius: 2, fontWeight: 800, textTransform: 'none', boxShadow: 'none' }}>Get My Price</Button></Box>
        </Container>

        <Container maxWidth="md" sx={{ pb: { xs: 5, md: 7 } }}>
          <Typography component="h2" sx={{ color: ink, fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 26, fontWeight: 800, mb: 2 }}>Pricing questions</Typography>
          {faqs.map(({ q, a }) => <Accordion key={q} disableGutters elevation={0} sx={{ border: `1px solid ${line}`, borderRadius: '10px !important', mb: 1, '&:before': { display: 'none' } }}><AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography sx={{ color: '#14243A', fontWeight: 700, fontSize: 14 }}>{q}</Typography></AccordionSummary><AccordionDetails><Typography sx={{ color: '#596A80', fontSize: 13, lineHeight: 1.65 }}>{a}</Typography></AccordionDetails></Accordion>)}
        </Container>

        <Box ref={demoRef}><BookDemo title="Get Your Price" description="Tell us about your team and we’ll show AcoLead on your own leads, then send you a clear price." submitLabel="Get My Price" /></Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default PricingPage;
